import { Component, OnDestroy, effect, inject } from '@angular/core';
import { navItemsProperties } from './_navigation-items';
import { fadeInGlideRight } from '../../shared/utils/animations';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { NavigationLinkProperties } from '../../shared/model/interfaces/navigation-link-properties.interface';
import { EffectRef } from '@angular/core';

import {
  INavData,
  SidebarComponent,
  SidebarNavComponent,
} from '@coreui/angular-pro';
import { CommonModule } from '@angular/common';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { SidebarNavHelper } from '@coreui/angular-pro';
import { SystemService } from '@shared/service/system/system.service';
import { AuthService } from '../../auth/service/auth.service';
import { PermissionMapping } from '@shared/model/interfaces/permission-mapping.interface';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  SidebarTooltipDirective,
  DropdownItem,
} from './sidebar-tooltip.directive';
import { UrlSyncService } from '../../guards/service/url-sync.service';

@Component({
  selector: 'app-left-panel',
  templateUrl: './left-panel.component.html',
  styleUrls: ['./left-panel.component.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    NgScrollbarModule,
    SidebarComponent,
    SidebarNavComponent,
    SidebarTooltipDirective,
    RouterModule,
  ],
  animations: [fadeInGlideRight()],
  providers: [SidebarNavHelper],
})
export class LeftPanelComponent implements OnDestroy {
  private translate = inject(TranslateService);
  systemService = inject(SystemService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private urlSyncService = inject(UrlSyncService);

  private navItemsProperties = navItemsProperties;
  public navItems: INavData[] = [];
  animationDuration = { value: '', params: { duration: '500ms' } };
  visible = true;
  visibility = false;
  expanded = false; // Default is unexpanded (showing only icons)
  disabled = false; // Disabled when dashboard modals are open
  private langChangeSubscription?: Subscription;
  private urlSyncSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private sidebarDisabledSubscription?: Subscription;
  private initializationEffect?: EffectRef;
  currentUrl = '';

  constructor() {
    // Initialize current URL
    this.currentUrl = this.router.url.split('?')[0].split('#')[0];
    // Subscribe to sidebar expanded events
    this.systemService.sidebarExpanded$.subscribe((expanded) => {
      this.expanded = expanded;
    });

    // CRITICAL: Subscribe to sidebar disabled state (when dashboard modals are open)
    // This prevents navigation while modals are active, preventing $rootScope corruption
    this.sidebarDisabledSubscription = this.systemService.sidebarDisabled$.subscribe((disabled) => {
      this.disabled = disabled;
    });

    // Subscribe to URL changes from iframe-initiated navigation
    this.urlSyncSubscription = this.urlSyncService.urlChanged.subscribe((url) => {
      this.currentUrl = url;
      this.updateSidebarActiveStates(url);
    });

    // Subscribe to normal router navigation events
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl = event.url.split('?')[0].split('#')[0];
      });

    this.initializationEffect = effect(() => {
      const ready = this.systemService.isInitialized();
      const projects = this.systemService.projects();

      if (!ready || projects.length === 0) {
        return;
      }

      const projectId = this.systemService.selectedProject;
      if (!projectId) {
        console.warn(
          ' >> Invalid selected project after ready state, projectId:',
          projectId,
        );
        return;
      }

      const projectValid =
        !!projectId && projects.some((project) => project.id === projectId);

      if (!projectValid) {
        console.warn(
          ' >> Invalid selected project after ready state, projectValid:',
          projectValid,
        );
        return;
      }

      this.buildNavigationMenu();
    });

    // Subscribe to URL changes from iframe-initiated navigation
    this.urlSyncSubscription = this.urlSyncService.urlChanged.subscribe((url) => {
      this.currentUrl = url;
      this.updateSidebarActiveStates(url);
    });

    // Subscribe to normal router navigation events
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const cleanUrl = event.url.split('?')[0].split('#')[0];
        this.currentUrl = cleanUrl;
      });
  }

  private updateSidebarActiveStates(currentUrl: string): void {
    // Update active states for CoreUI sidebar navigation (expanded state)
    const sidebarLinks = document.querySelectorAll('c-sidebar-nav a.c-sidebar-nav-link');
    sidebarLinks.forEach((link) => {
      const href = link.getAttribute('href');
      if (href) {
        const cleanHref = href.split('?')[0].split('#')[0];
        if (cleanHref === currentUrl) {
          link.classList.add('c-active');
        } else {
          link.classList.remove('c-active');
        }
      }
    });
  }

  isLinkActive(url: string | unknown[] | undefined): boolean {
    if (!url || typeof url !== 'string') return false;
    return this.currentUrl === url;
  }

  private async buildNavigationMenu() {
    const navItems = this.filterNavigationItemsByPermissions(navItemsProperties);
    const keys = this.collectTranslationKeys(navItems);
    const translations = await this.waitForTranslationsToLoad(keys);
    this.navItems = this.translateNavItemsRecursively(navItems, translations);
    this.visibility = true;

    this.langChangeSubscription?.unsubscribe();
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.translate.get(keys).subscribe((res) => {
        this.navItems = this.translateNavItemsRecursively(navItems, res);
      });
    });
  }

  private async waitForTranslationsToLoad(
    keys: string[],
    retries = 20,
  ): Promise<Record<string, string>> {
    while (retries-- > 0) {
      const translations = await this.translate.get(keys).toPromise();

      const allTranslated = keys.every((key) => translations[key] !== key);
      if (allTranslated) {
        return translations;
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    console.warn('Translation timeout — falling back to keys');
    return this.translate.get(keys).toPromise();
  }

  ngOnDestroy(): void {
    this.langChangeSubscription?.unsubscribe();
    this.urlSyncSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
    this.sidebarDisabledSubscription?.unsubscribe();

    if (this.initializationEffect) {
      this.initializationEffect.destroy();
    }
  }

  private collectTranslationKeys(items: INavData[]): string[] {
    let keys: string[] = [];
    items.forEach((item) => {
      if (item.name) {
        keys.push(item.name);
      }
      if (item.children) {
        keys = keys.concat(this.collectTranslationKeys(item.children));
      }
    });
    return keys;
  }

  private translateNavItemsRecursively(
    items: INavData[],
    translations: Record<string, string>,
  ): INavData[] {
    const selectedProjectId = this.systemService.selectedProject;
    const selectedCompany = selectedProjectId
      ? this.systemService.getCompanyIdFromProject(selectedProjectId) || this.systemService.selectedCompany
      : this.systemService.selectedCompany;

    return items.map((item) => ({
      ...item,
      name: translations[item.name ?? ''] || item.name,
      url:
        typeof item.url === 'string'
          ? item.url.replace(':id', selectedCompany)
          : item.url,
      children: item.children
        ? this.translateNavItemsRecursively(item.children, translations)
        : undefined,
    }));
  }

  private checkNavigationItemPermission(
    permissions: PermissionMapping[] | undefined,
    context: Record<string, unknown>,
  ): boolean {
    return (
      !permissions ||
      permissions.every(({ source, key, required = true }) => {
        const sourceContext = context[source as string];
        if (!sourceContext || typeof sourceContext !== 'object') {
          return !required;
        }

        const record = sourceContext as Record<string, unknown>;
        const value = record[key];

        return required ? value === true : value !== true;
      })
    );
  }

  private filterNavigationItemsByPermissions(
    items: NavigationLinkProperties[],
  ): INavData[] {
    const recursiveFilter = (items: NavigationLinkProperties[]): INavData[] => {
      const context = this.authService.getPermissionsContext();

      return items.reduce<INavData[]>((collection, item) => {
        const hasPermission =
          !item.permission ||
          this.checkNavigationItemPermission(item.permission, context);
        if (!hasPermission) return collection;

        const filteredChildren = item.children
          ? this.filterNavigationItemsByPermissions(item.children)
          : undefined;

        if (
          !item.children ||
          (filteredChildren && filteredChildren.length > 0)
        ) {
          const { permission, ...newItem } = item;
          if (filteredChildren) newItem.children = filteredChildren;
          collection.push(newItem);
        }

        return collection;
      }, []);
    };

    return recursiveFilter(items);
  }

  getDropdownItemNames(item: INavData): DropdownItem[] {
    if (!item.children || item.children.length === 0) {
      return [];
    }

    return item.children.map((child) => ({
      name: child.name || '',
      url: (child.url as string) || '',
      icon: (child.icon as string) || undefined,
    }));
  }
}
